/// <reference types="google.maps" />
import { FlightTextInput } from '@flybits/design-system';
import GoogleMapWrapper from 'pages/LocationManagement/MapContainer/GoogleMapWrapper';
import React, { useEffect, useRef, useState } from 'react';
type PlaceData = {
  formatted_address?: string;
  street_number?: string;
  route?: string;
  city?: string;
  state?: string;
  postalCode?: string;
};
import './AddressAutocomplete.scss';

const ADDRESS_COMPLETE_INPUT = 'address-complete-input';
const CLASSES = {
  ADDRESS_LIST: `${ADDRESS_COMPLETE_INPUT}__address-list`,
  ADDRESS_LIST_SELECTION: `${ADDRESS_COMPLETE_INPUT}__address-list-selection`,
};

type AddressAutocompleteProps = {
  onSelect: (place: PlaceData) => void;
  onBlur: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ onSelect, onBlur }) => {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google?.maps?.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoder.current = new window.google.maps.Geocoder();
        clearInterval(interval);
      }
    }, 100); // every 100ms
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.trim() && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: value, componentRestrictions: { country: 'us' }, types: ['address'] },
        (results) => {
          setPredictions(results || []);
          if (!results) {
            onSelect({ formatted_address: '', street_number: '', route: '', city: '', state: '', postalCode: '' });
            setInputValue('');
          } else {
            setInputValue(value);
          }
        },
      );
    } else {
      setPredictions([]);
      onSelect({ formatted_address: '', street_number: '', route: '', city: '', state: '', postalCode: '' });
      setInputValue('');
    }
  };
  const handleSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    setPredictions([]);
    if (geocoder.current) {
      geocoder.current.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const result = results[0];
          const components = result.address_components;
          const getComponent = (type: string) => components.find((c) => c.types.includes(type))?.long_name;
          const getComponentShort = (type: string) => components.find((c) => c.types.includes(type))?.short_name;
          const placeData: PlaceData = {
            // Retrieving parts of the address selected.
            formatted_address: result.formatted_address,
            street_number: getComponent('street_number') || '',
            route: getComponent('route') || '',
            city: getComponent('locality') || getComponent('sublocality') || '',
            state: getComponentShort('administrative_area_level_1') || '',
            postalCode: getComponent('postal_code') || '',
          };
          setInputValue(placeData.street_number + ' ' + placeData.route);
          onSelect(placeData);
        }
      });
    }
  };

  return (
    <GoogleMapWrapper>
      <div style={{ position: 'relative', width: '100%' }}>
        <FlightTextInput
          width="100%"
          type="text"
          name="address"
          value={inputValue}
          onBlur={onBlur}
          onChange={handleInputChange}
          data-testid="merchant-address-input"
        />
        {predictions.length > 0 && (
          <ul className={CLASSES.ADDRESS_LIST}>
            {predictions.map((prediction) => (
              <button
                className={CLASSES.ADDRESS_LIST_SELECTION}
                key={prediction.place_id}
                onClick={() => handleSelect(prediction)}
              >
                {prediction.description}
              </button>
            ))}
          </ul>
        )}
      </div>
    </GoogleMapWrapper>
  );
};
export default AddressAutocomplete;
